import _extends from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faClock } from "@fortawesome/pro-regular-svg-icons";
import { formatStripDateTime } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { DicomBadge } from "./dicom-badge";
import { ReportAiTags } from "./report-ai-tags";
import { ReportVersionPanel } from "./report-version-panel";
import { ReportVersionTabs } from "./report-version-tabs";
/** Expandable report card matching the RadiologyReportCard strip-and-detail visual style. */
export function ReportItem(_ref) {
  var _versions$0$id, _versions$, _versions$find, _activeVersion$aiTags, _activeVersion$id;
  var heading = _ref.heading,
    modality = _ref.modality,
    examDate = _ref.examDate,
    updatedAt = _ref.updatedAt,
    versions = _ref.versions,
    reportId = _ref.reportId,
    studyId = _ref.studyId,
    onApplyTag = _ref.onApplyTag,
    applyingTagId = _ref.applyingTagId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var _useState2 = useState((_versions$0$id = (_versions$ = versions[0]) === null || _versions$ === void 0 ? void 0 : _versions$.id) !== null && _versions$0$id !== void 0 ? _versions$0$id : ""),
    activeVersionId = _useState2[0],
    setActiveVersionId = _useState2[1];
  var timestamp = formatStripDateTime(updatedAt);
  var activeVersion = (_versions$find = versions.find(function (v) {
    return v.id === activeVersionId;
  })) !== null && _versions$find !== void 0 ? _versions$find : versions[0];
  var aiTags = (_activeVersion$aiTags = activeVersion === null || activeVersion === void 0 ? void 0 : activeVersion.aiTags) !== null && _activeVersion$aiTags !== void 0 ? _activeVersion$aiTags : [];
  var tagsTitle = t("label.ai_tags", {
    defaultValue: "AI Tags"
  });
  var applyProps = onApplyTag ? {
    onApplyTag: onApplyTag,
    applyingTagId: applyingTagId !== null && applyingTagId !== void 0 ? applyingTagId : null
  } : {};
  var toggle = function toggle() {
    trackEvent(ANALYTICS_EVENTS.REPORT_ITEM_TOGGLE);
    setIsExpanded(function (prev) {
      var _versions$0$id2, _versions$2;
      if (!prev) setActiveVersionId((_versions$0$id2 = (_versions$2 = versions[0]) === null || _versions$2 === void 0 ? void 0 : _versions$2.id) !== null && _versions$0$id2 !== void 0 ? _versions$0$id2 : "");
      return !prev;
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col flex-1 min-w-0 bg-sds-base-default rounded-sds-200 border border-sds-base-default shadow-sds-200 overflow-hidden my-1 mr-2"
  }, /*#__PURE__*/React.createElement("div", {
    role: "button",
    tabIndex: 0,
    "aria-expanded": isExpanded,
    "data-testid": "report-item-toggle",
    className: "flex items-center justify-between px-3.5 py-2.5 gap-2 w-full cursor-pointer select-none hover:bg-sds-brand-tertiary transition-colors duration-150",
    onClick: toggle,
    onKeyDown: function onKeyDown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggle();
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 min-w-0 flex-1"
  }, modality && /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary shrink-0"
  }, modality), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default uppercase truncate min-w-0 text-left",
    title: heading
  }, heading || t("label.untitled_report", {
    defaultValue: "Untitled Report"
  })), examDate && /*#__PURE__*/React.createElement("span", {
    className: "inline-flex items-center gap-2 text-style-label-base text-sds-base-secondary whitespace-nowrap shrink-0"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("label.exam_date", {
    defaultValue: "Exam Date"
  })), examDate)), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 shrink-0"
  }, reportId != null && /*#__PURE__*/React.createElement(DicomBadge, {
    reportId: reportId,
    studyId: studyId
  }), timestamp && /*#__PURE__*/React.createElement("span", {
    className: "inline-flex items-center gap-1.5 text-style-label-base text-sds-base-tertiary whitespace-nowrap"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faClock,
    className: "w-4 h-4 shrink-0"
  }), timestamp), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronDown,
    className: "w-3.5 h-3.5 shrink-0 text-sds-base-tertiary transition-transform duration-200 ease-in-out".concat(isExpanded ? " rotate-180" : "")
  }))), !isExpanded && aiTags.length > 0 && /*#__PURE__*/React.createElement(ReportAiTags, _extends({
    tags: aiTags,
    title: tagsTitle
  }, applyProps)), /*#__PURE__*/React.createElement("div", {
    className: "grid grid-cols-1 transition-[grid-template-rows] duration-200 ease-in-out".concat(isExpanded ? " grid-rows-[1fr]" : " grid-rows-[0fr]")
  }, /*#__PURE__*/React.createElement("div", {
    className: "min-h-0 overflow-hidden border-t border-sds-base-default"
  }, versions.length > 1 && /*#__PURE__*/React.createElement(ReportVersionTabs, {
    versions: versions,
    activeVersionId: (_activeVersion$id = activeVersion === null || activeVersion === void 0 ? void 0 : activeVersion.id) !== null && _activeVersion$id !== void 0 ? _activeVersion$id : "",
    onVersionChange: setActiveVersionId
  }), activeVersion ? /*#__PURE__*/React.createElement(ReportVersionPanel, {
    key: activeVersion.id,
    version: activeVersion
  }) : /*#__PURE__*/React.createElement("div", {
    className: "px-3.5 py-3"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-base text-sds-base-tertiary"
  }, t("label.no_report_details", {
    defaultValue: "No report details available"
  }))), aiTags.length > 0 && /*#__PURE__*/React.createElement(ReportAiTags, _extends({
    tags: aiTags,
    title: tagsTitle
  }, applyProps)))));
}